import { AsyncButton } from '@c42/component-library';
import { Button, TextField, Typography } from '@mui/material';
import * as React from 'react';
import Text from '../../../shared/Text';
import { sendEmptyTextIfNull } from '../../utils/input-handlers';
import FormValidationMessage from '../form-validation-message';
import LoginWrapper from '../login-wrapper';
import { useAuthData, useResetForm, useSetAuthData, useSubmitLogin } from '../providers/auth/hooks';
import { useResetPasswordData } from '../providers/resetPassword/hooks';
import { FormFieldWrapper, FormSubmitWrapper, FormWrapper } from '../shared-form-components';

const LoginStepPassword: React.FC = () => {
  const submitLogin = useSubmitLogin();
  const resetForm = useResetForm();
  const setAuthData = useSetAuthData();
  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  const {
    username,
    password,
    totp,
    isUsingTotp,
    loginError: error,
    isAuthenticating: isSubmittingPassword,
  } = useAuthData();
  const { notification } = useResetPasswordData();

  const submitForm = (e?: React.FormEvent) => {
    if (e && 'preventDefault' in e) {
      e.preventDefault();
    }
    setHasSubmitted(true);
    submitLogin();
  };

  const resetFormHandler = (e?: React.FormEvent) => {
    if (e && 'preventDefault' in e) {
      e.preventDefault();
    }
    resetForm();
  };

  const changePasswordHandler: React.FormEventHandler = (event) => {
    event.persist?.();
    setAuthData((current) => ({
      ...current,
      password: (event.target as HTMLInputElement).value,
    }));
  };

  const changeTotpHandler: React.FormEventHandler = (event) => {
    event.persist?.();
    setAuthData((current) => ({
      ...current,
      totp: (event.target as HTMLInputElement).value,
    }));
  };

  const isPasswordEmpty = !password;
  const isPasswordDirty = password !== null && isPasswordEmpty;

  const passwordErrorText =
    isPasswordDirty && isPasswordEmpty && hasSubmitted ? Text.get('_validate_password_required') : '';

  const isTotpEmpty = !totp;
  const isTotpDirty = totp !== null && isTotpEmpty;

  const totpErrorText = isTotpDirty && isTotpEmpty && hasSubmitted ? Text.get('_validate_totp_required') : '';

  let validationError = error;
  if (error && isUsingTotp) {
    validationError = Text.get('login_error_incorrect_totp');
  }

  return (
    <LoginWrapper showForgotPasswordLink showSupportLink>
      <FormWrapper>
        <form onSubmit={submitForm} name="loginFrm" data-test="login-step-password-form" noValidate>
          <FormValidationMessage
            message={validationError || notification}
            type={validationError ? 'error' : 'success'}
            dataTest="validation-msg"
          />

          <FormFieldWrapper>
            <TextField
              autoComplete="off"
              disabled
              fullWidth
              id="username"
              inputProps={{ 'data-test': 'username' }}
              label={Text.get('_placeholder_username_or_email')}
              value={username || ''}
            />
          </FormFieldWrapper>

          <FormFieldWrapper>
            <TextField
              autoComplete="off"
              autoFocus
              disabled={isSubmittingPassword}
              error={Boolean(passwordErrorText)}
              fullWidth
              helperText={passwordErrorText}
              id="password"
              inputProps={{ 'data-test': 'password' }}
              label={Text.get('_placeholder_password')}
              onBlur={sendEmptyTextIfNull.bind(null, changePasswordHandler, password)}
              onChange={changePasswordHandler}
              type="password"
              value={password || ''}
            />
          </FormFieldWrapper>

          {isUsingTotp && (
            <div>
              <FormFieldWrapper>
                <TextField
                  autoComplete="off"
                  disabled={isSubmittingPassword}
                  error={Boolean(totpErrorText)}
                  fullWidth
                  helperText={totpErrorText}
                  id="totp"
                  inputProps={{ 'data-test': 'totp-code', maxLength: 6 }}
                  label={Text.get('_placeholder_enter_code')}
                  onBlur={sendEmptyTextIfNull.bind(null, changeTotpHandler, totp)}
                  onChange={changeTotpHandler}
                  type="text"
                  value={totp || ''}
                />
              </FormFieldWrapper>
              <Typography color="textSecondary" variant="body2">
                {Text.get('login_totp_code_tooltip')}
              </Typography>
            </div>
          )}

          <FormSubmitWrapper>
            <AsyncButton
              data-test="submit"
              isLoading={isSubmittingPassword}
              type="submit"
              variant="contained"
            >
              {Text.get('login_button_sign_in')}
            </AsyncButton>
          </FormSubmitWrapper>

          <FormFieldWrapper>
            <Button
              data-test="signin-different-account"
              onClick={resetFormHandler}
              size="small"
              variant="text"
            >
              {Text.get('sso_signin_different_account')}
            </Button>
          </FormFieldWrapper>
        </form>
      </FormWrapper>
    </LoginWrapper>
  );
};

export default LoginStepPassword;
